import React, { Component } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { findOptionsForSelectFilter } from '../../util/search';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import classNames from 'classnames';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { formatMoney } from '../../util/currency';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import {
  Page,
  NamedLink,
  NamedRedirect,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  BookingPanel,
} from '../../components';
import { TopbarContainer, NotFoundPage } from '../../containers';

import { sendEnquiry, fetchTransactionLineItems, setInitialValues } from './ListingPage.duck';
import SectionImages from './SectionImages';
import SectionAvatar from './SectionAvatar';
import SectionHeading from './SectionHeading';
import SectionDescriptionMaybe from './SectionDescriptionMaybe';
import SectionFeaturesMaybe from './SectionFeaturesMaybe';
import SectionReviews from './SectionReviews';
import SectionHostMaybe from './SectionHostMaybe';
import SectionRulesMaybe from './SectionRulesMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import css from './ListingPage.module.css';
import ContactPanel from '../../components/ContactPanel/ContactPanel';
// import GaugeChart from 'react-gauge-chart';
import * as ChartModuleMore from 'highcharts/highcharts-more.js';
import HCSoldGauge from 'highcharts/modules/solid-gauge';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
ChartModuleMore(Highcharts);
HCSoldGauge(Highcharts);

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID, Money } = sdkTypes;

const priceData = (price, intl) => {
  if (price && price instanceof Money && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const categoryLabel = (categories, key) => {
  const cat = categories.find(c => c.key === key);
  return cat ? cat.label : key;
};

export class ListingPageComponent extends Component {
  constructor(props) {
    super(props);
    const { enquiryModalOpenForListingId, params } = props;
    this.state = {
      pageClassNames: [],
      imageCarouselOpen: false,
      enquiryModalOpen: enquiryModalOpenForListingId === params.id,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onContactUser = this.onContactUser.bind(this);
    this.onSubmitEnquiry = this.onSubmitEnquiry.bind(this);
  }

  handleSubmit(values) {
    const {
      history,
      getListing,
      params,
      callSetInitialValues,
      onInitializeCardPaymentData,
    } = this.props;
    const listingId = new UUID(params.id);
    const listing = getListing(listingId);

    const { bookingDates, ...bookingData } = values;

    const initialValues = {
      listing,
      bookingData,
      bookingDates: {
        bookingStart: bookingDates.startDate,
        bookingEnd: bookingDates.endDate,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !this.props.currentUser;

    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  }

  onContactUser() {
    const { currentUser, history, callSetInitialValues, params, location } = this.props;

    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };

      // We need to log in before showing the modal, but first we need to ensure
      // that modal does open when user is redirected back to this listingpage
      callSetInitialValues(setInitialValues, { enquiryModalOpenForListingId: params.id });

      // signup and return back to listingPage.
      history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}), state);
    } else {
      this.setState({ enquiryModalOpen: true });
    }
  }

  onSubmitEnquiry(values) {
    const { history, params, onSendEnquiry } = this.props;
    const routes = routeConfiguration();
    const listingId = new UUID(params.id);
    const { message } = values;
    onSendEnquiry(listingId, message.trim())
      .then(txId => {
        this.setState({ enquiryModalOpen: false });

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  }

  render() {
    const {
      unitType,
      isAuthenticated,
      currentUser,
      getListing,
      getOwnListing,
      intl,
      onManageDisableScrolling,
      params: rawParams,
      location,
      scrollingDisabled,
      showListingError,
      reviews,
      fetchReviewsError,
      sendEnquiryInProgress,
      sendEnquiryError,
      timeSlots,
      fetchTimeSlotsError,
      filterConfig,
      onFetchTransactionLineItems,
      lineItems,
      fetchLineItemsInProgress,
      fetchLineItemsError,
      disableAllRootedRent,
    } = this.props;

    const listingId = new UUID(rawParams.id);
    const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));

    const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
    const params = { slug: listingSlug, ...rawParams };

    const listingType = isDraftVariant
      ? LISTING_PAGE_PARAM_TYPE_DRAFT
      : LISTING_PAGE_PARAM_TYPE_EDIT;
    const listingTab = isDraftVariant ? 'photos' : 'description';

    const isApproved =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

    const pendingIsApproved = isPendingApprovalVariant && isApproved;

    // If a /pending-approval URL is shared, the UI requires
    // authentication and attempts to fetch the listing from own
    // listings. This will fail with 403 Forbidden if the author is
    // another user. We use this information to try to fetch the
    // public listing.
    const pendingOtherUsersListing =
      (isPendingApprovalVariant || isDraftVariant) &&
      showListingError &&
      showListingError.status === 403;
    const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

    if (shouldShowPublicListingPage) {
      return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
    }

    const {
      description = '',
      geolocation = null,
      price = null,
      title = '',
      publicData,
    } = currentListing.attributes;
    const address = publicData && publicData.location ? publicData.location.address : null;
    const richTitle = (
      <span>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
          longWordClass: css.longWord,
        })}
      </span>
    );

    const bookingTitle = (
      <FormattedMessage id="ListingPage.bookingTitle" values={{ title: richTitle }} />
    );
    const bookingSubTitle = intl.formatMessage({ id: 'ListingPage.bookingSubTitle' });

    const topbar = <TopbarContainer />;

    if (showListingError && showListingError.status === 404) {
      // 404 listing not found

      return <NotFoundPage />;
    } else if (showListingError) {
      // Other error in fetching listing

      const errorTitle = intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      });

      return (
        <Page title={errorTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.errorText}>
                <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    } else if (!currentListing.id) {
      // Still loading the listing

      const loadingTitle = intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      });

      return (
        <Page title={loadingTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.loadingText}>
                <FormattedMessage id="ListingPage.loadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    }

    const handleViewPhotosClick = e => {
      // Stop event from bubbling up to prevent image click handler
      // trying to open the carousel as well.
      e.stopPropagation();
      this.setState({
        imageCarouselOpen: true,
      });
    };
    const authorAvailable = currentListing && currentListing.author;
    const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
    const isOwnListing =
      userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;
    const showContactUser = authorAvailable && (!currentUser || (currentUser && !isOwnListing));

    const currentAuthor = authorAvailable ? currentListing.author : null;
    const ensuredAuthor = ensureUser(currentAuthor);

    // When user is banned or deleted the listing is also deleted.
    // Because listing can be never showed with banned or deleted user we don't have to provide
    // banned or deleted display names for the function
    const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

    const { formattedPrice, priceTitle } = priceData(price, intl);

    const handleBookingSubmit = values => {
      const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
      if (isOwnListing || isCurrentlyClosed) {
        window.scrollTo(0, 0);
      } else {
        this.handleSubmit(values);
      }
    };

    const listingImages = (listing, variantName) =>
      (listing.images || [])
        .map(image => {
          const variants = image.attributes.variants;
          const variant = variants ? variants[variantName] : null;

          // deprecated
          // for backwards combatility only
          const sizes = image.attributes.sizes;
          const size = sizes ? sizes.find(i => i.name === variantName) : null;

          return variant || size;
        })
        .filter(variant => variant != null);

    const facebookImages = listingImages(currentListing, 'facebook');
    const twitterImages = listingImages(currentListing, 'twitter');
    const schemaImages = JSON.stringify(facebookImages.map(img => img.url));
    const siteTitle = config.siteTitle;
    const schemaTitle = intl.formatMessage(
      { id: 'ListingPage.schemaTitle' },
      { title, price: formattedPrice, siteTitle }
    );

    const hostLink = (
      <NamedLink
        className={css.authorNameLink}
        name="ListingPage"
        params={params}
        to={{ hash: '#host' }}
      >
        {authorDisplayName}
      </NamedLink>
    );

    const amenityOptions = findOptionsForSelectFilter('amenities', filterConfig);
    const categoryOptions = findOptionsForSelectFilter('category', filterConfig);
    const category =
      publicData && publicData.category ? (
        <span>
          {categoryLabel(categoryOptions, publicData.category)}
          <span className={css.separator}>•</span>
        </span>
      ) : null;

    function removeCents(value) {
      if (!value || typeof value !== 'string') {
        return '0';
      } else {
        return value.replace(/\..*/g, '');
      }
    }

    const { householdAnnualIncome } = currentUser?.attributes?.profile?.protectedData || {};
    const householdAnnualIncomeFloat = parseFloat(householdAnnualIncome) || 0;
    const householdAnnualIncomeCents = !!householdAnnualIncomeFloat
      ? householdAnnualIncomeFloat
      : 0;
    const { effectiveRent } = publicData || {};
    let annualHouseholdIncomePercentage =
      !!householdAnnualIncomeCents && !!effectiveRent
        ? (effectiveRent * 12) / householdAnnualIncomeCents
        : null;
    annualHouseholdIncomePercentage =
      annualHouseholdIncomePercentage > 100
        ? 100
        : annualHouseholdIncomePercentage < 0
        ? 0
        : annualHouseholdIncomePercentage;
    const annualHouseholdIncomePercentageRounded = Math.round(annualHouseholdIncomePercentage);
    const annualHouseholdIncomePercentageFormatted = (
      <span className={css.householdIncomePercentage}>
        {annualHouseholdIncomePercentageRounded}
      </span>
    );
    let annualHouseholdIncomePercentageLabel = null;
    let affordabilityEstimate = null;
    let chartPercentage = null;
    // Mapping values in a range
    // scale = (D-C)/(B-A)
    // offset = -A*(D-C)/(B-A) + C
    // q = p*scale + offset
    if (annualHouseholdIncomePercentageRounded < 27) {
      affordabilityEstimate = 'veryAffordable';
      annualHouseholdIncomePercentageLabel = (
        <span className={css.annualHouseholdIncomePercentageLabel} style={{ color: '#6FEF4D' }}>
          <FormattedMessage
            id="ListingPage.veryAffordable"
            values={{ lineBreak: <br />, percentage: annualHouseholdIncomePercentageFormatted }}
          />
        </span>
      );
      const A = 0;
      const B = 26;
      const C = 0;
      const D = 24;
      const p = annualHouseholdIncomePercentageRounded;
      const scale = (D - C) / (B - A);
      const offset = (-A * (D - C)) / (B - A) + C;
      const q = p * scale + offset;
      chartPercentage = q / 100;
    } else if (
      annualHouseholdIncomePercentageRounded >= 27 &&
      annualHouseholdIncomePercentageRounded < 30
    ) {
      affordabilityEstimate = 'affordable';
      annualHouseholdIncomePercentageLabel = (
        <span className={css.annualHouseholdIncomePercentageLabel} style={{ color: '#42942E' }}>
          <FormattedMessage
            id="ListingPage.affordable"
            values={{ lineBreak: <br />, percentage: annualHouseholdIncomePercentageFormatted }}
          />
        </span>
      );
      const A = 27;
      const B = 29;
      const C = 26;
      const D = 49;
      const p = annualHouseholdIncomePercentageRounded;
      const scale = (D - C) / (B - A);
      const offset = (-A * (D - C)) / (B - A) + C;
      const q = p * scale + offset;
      chartPercentage = q / 100;
    } else if (
      annualHouseholdIncomePercentageRounded >= 30 &&
      annualHouseholdIncomePercentageRounded <= 35
    ) {
      affordabilityEstimate = 'stretch';
      annualHouseholdIncomePercentageLabel = (
        <span className={css.annualHouseholdIncomePercentageLabel} style={{ color: '#EF8D35' }}>
          <FormattedMessage
            id="ListingPage.stretch"
            values={{ lineBreak: <br />, percentage: annualHouseholdIncomePercentageFormatted }}
          />
        </span>
      );
      const A = 30;
      const B = 35;
      const C = 51;
      const D = 74;
      const p = annualHouseholdIncomePercentageRounded;
      const scale = (D - C) / (B - A);
      const offset = (-A * (D - C)) / (B - A) + C;
      const q = p * scale + offset;
      chartPercentage = q / 100;
    } else if (annualHouseholdIncomePercentageRounded > 35) {
      affordabilityEstimate = 'burdened';
      annualHouseholdIncomePercentageLabel = (
        <span className={css.annualHouseholdIncomePercentageLabel} style={{ color: '#E03023' }}>
          <FormattedMessage
            id="ListingPage.burdened"
            values={{ lineBreak: <br />, percentage: annualHouseholdIncomePercentageFormatted }}
          />
        </span>
      );
      const A = 35;
      const B = 100;
      const C = 76;
      const D = 99;
      const p = annualHouseholdIncomePercentageRounded;
      const scale = (D - C) / (B - A);
      const offset = (-A * (D - C)) / (B - A) + C;
      const q = p * scale + offset;
      chartPercentage = q / 100;
    }
    const veryAffordableInfo = (
      <div
        className={classNames(css.affordabilityEstimateInfo, {
          [css.affordabilityEstimateInfoHighlightVeryAffordable]:
            !!householdAnnualIncomeCents && affordabilityEstimate === 'veryAffordable',
        })}
      >
        <span className={css.veryAffordableRange}>
          {intl.formatMessage({ id: 'ListingPage.veryAffordableRange' })}
        </span>
        <span>
          {!!householdAnnualIncomeCents && affordabilityEstimate === 'veryAffordable'
            ? intl.formatMessage(
                { id: 'ListingPage.unitCostInfo' },
                { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
              )
            : null}
          {intl.formatMessage(
            { id: 'ListingPage.veryAffordableInfo' },
            { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
          )}
        </span>
      </div>
    );
    const affordableInfo = (
      <div
        className={classNames(css.affordabilityEstimateInfo, {
          [css.affordabilityEstimateInfoHighlightAffordable]:
            !!householdAnnualIncomeCents && affordabilityEstimate === 'affordable',
        })}
      >
        <span className={css.affordableRange}>
          {intl.formatMessage({ id: 'ListingPage.affordableRange' })}
        </span>
        <span>
          {!!householdAnnualIncomeCents && affordabilityEstimate === 'affordable'
            ? intl.formatMessage(
                { id: 'ListingPage.unitCostInfo' },
                { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
              )
            : null}
          {intl.formatMessage(
            { id: 'ListingPage.affordableInfo' },
            { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
          )}
        </span>
      </div>
    );
    const stretchInfo = (
      <div
        className={classNames(css.affordabilityEstimateInfo, {
          [css.affordabilityEstimateInfoHighlightStretch]:
            !!householdAnnualIncomeCents && affordabilityEstimate === 'stretch',
        })}
      >
        <span className={css.stretchRange}>
          {intl.formatMessage({ id: 'ListingPage.stretchRange' })}
        </span>
        <span>
          {!!householdAnnualIncomeCents && affordabilityEstimate === 'stretch'
            ? intl.formatMessage(
                { id: 'ListingPage.unitCostInfo' },
                { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
              )
            : null}
          {intl.formatMessage(
            { id: 'ListingPage.stretchInfo' },
            { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
          )}
        </span>
      </div>
    );
    const burdenedInfo = (
      <div
        className={classNames(css.affordabilityEstimateInfo, {
          [css.affordabilityEstimateInfoHighlightBurdened]:
            !!householdAnnualIncomeCents && affordabilityEstimate === 'burdened',
        })}
      >
        <span className={css.burdenedRange}>
          {intl.formatMessage({ id: 'ListingPage.burdenedRange' })}
        </span>
        <span>
          {!!householdAnnualIncomeCents && affordabilityEstimate === 'burdened'
            ? intl.formatMessage(
                { id: 'ListingPage.unitCostInfo' },
                { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
              )
            : null}
          {intl.formatMessage(
            { id: 'ListingPage.burdenedInfo' },
            { annualHouseholdIncomePercentage: annualHouseholdIncomePercentageFormatted }
          )}
        </span>
      </div>
    );

    const options = {
      chart: {
        type: 'gauge',
        backgroundColor: 'transparent',
        plotBackgroundColor: null,
        plotBackgroundImage: null,
        plotBorderWidth: 0,
        plotShadow: false,
        height: '200',
        margin: [0, 0, -100, 0],
        className: css.affordabilityChart,
      },
      credits: {
        enabled: false,
      },

      title: {
        text: '',
      },

      tooltip: {
        enabled: false,
      },

      pane: {
        startAngle: -90,
        endAngle: 90,
        background: null,
        center: ['50%', '50%'],
        size: '100%',
      },

      yAxis: {
        margin: [0, 0, 0, 0],
        padding: [0, 0, 0, 0],
        maxPadding: 0,
        reversed: true,
        min: 0,
        max: 100,
        tickPixelInterval: 100,
        tickPosition: 'inside',
        tickColor: Highcharts.defaultOptions.chart.backgroundColor || '#FFFFFF',
        tickLength: 0,
        tickWidth: 0,
        minorTickInterval: null,
        labels: {
          enabled: false,
        },
        lineWidth: 0,
        plotBands: [
          {
            from: 0,
            to: 25,
            color: !!householdAnnualIncomeCents ? '#6FEF4D' : '#b2b2b2',
            thickness: 80,
            borderRadius: '0%',
            borderWidth: '2',
            borderColor: '#FFFFFF',
          },
          {
            from: 25,
            to: 50,
            color: !!householdAnnualIncomeCents ? '#42942E' : '#b2b2b2',
            thickness: 80,
            borderRadius: '0%',
            borderWidth: '2',
            borderColor: '#FFFFFF',
          },
          {
            from: 50,
            to: 75,
            color: !!householdAnnualIncomeCents ? '#EF8D35' : '#b2b2b2',
            thickness: 80,
            borderRadius: '0%',
            borderWidth: '2',
            borderColor: '#FFFFFF',
          },
          {
            from: 75,
            to: 100,
            color: !!householdAnnualIncomeCents ? '#E03023' : '#b2b2b2',
            thickness: 80,
            borderRadius: '0%',
            borderWidth: '2',
            borderColor: '#FFFFFF',
          },
        ],
      },

      series: [
        {
          name: '',
          animation: false,
          data: !!householdAnnualIncomeCents ? [chartPercentage * 100] : [98],
          tooltip: {
            valueSuffix: ' %',
          },
          dataLabels: {
            enabled: false,
          },
          dial: {
            radius: '65%',
            backgroundColor: !!householdAnnualIncomeCents ? '#000000' : '#b2b2b2',
            baseWidth: 16,
            baseLength: '0%',
            rearLength: '0%',
          },
          pivot: {
            backgroundColor: !!householdAnnualIncomeCents ? '#000000' : '#b2b2b2',
            radius: 8,
          },
        },
      ],
    };

    const affordabilityEstimateCalculator = !!effectiveRent ? (
      <div className={css.annualHouseholdIncomePercentage}>
        {!!annualHouseholdIncomePercentageLabel ? (
          <div className={css.annualHouseholdIncomePercentageLabel}>
            {/* <GaugeChart
              id="affordability-chart"
              className={css.affordabilityChart}
              animate={false}
              nrOfLevels={4}
              arcWidth={0.5}
              arcPadding={0.01}
              cornerRadius={0}
              colors={
                !!householdAnnualIncomeCents
                  ? ['#6FEF4D', '#42942E', '#EF8D35', '#E03023']
                  : ['#b2b2b2']
              }
              percent={!!householdAnnualIncomeCents ? chartPercentage : 0.0}
              needleColor={!!householdAnnualIncomeCents ? '#000000' : '#b2b2b2'}
              needleBaseColor={!!householdAnnualIncomeCents ? '#000000' : '#b2b2b2'}
              hideText={true}
              // customNeedleComponent={!!annualHouseholdIncomePercentage ? null : <></>}
            /> */}
            {<HighchartsReact highcharts={Highcharts} options={options} />}
            {!!householdAnnualIncomeCents ? annualHouseholdIncomePercentageLabel : null}
          </div>
        ) : null}
        <div
          className={classNames(css.infoWrapper, {
            [css.infoWrapperDisabled]: !householdAnnualIncomeCents,
          })}
        >
          {veryAffordableInfo}
          {affordableInfo}
          {stretchInfo}
          {burdenedInfo}
        </div>
        {!!householdAnnualIncomeCents ? null : (
          <div className={classNames(css.noAffordabilityEstimate)}>
            <FormattedMessage
              id="ListingPage.noAffordabilityEstimate"
              values={{
                link: (
                  <NamedLink name="ProfileSettingsPage">
                    <FormattedMessage id="ListingPage.updateHouseholdIncome" />
                  </NamedLink>
                ),
              }}
            />
          </div>
        )}
        <div className={css.disclaimer}>
          <FormattedMessage id="ListingPage.affordabilityEstimateDisclaimer" />
        </div>
        <div className={css.note}>
          <FormattedMessage
            id="ListingPage.affordabilityEstimateNote"
            values={{
              link: (
                <NamedLink name="ProfileSettingsPage">
                  <FormattedMessage id="ListingPage.updateProfile" />
                </NamedLink>
              ),
              boldText: (
                <b>{intl.formatMessage({ id: 'ListingPage.affordabilityEstimateNoteBoldText' })}</b>
              ),
            }}
          />
        </div>
      </div>
    ) : null;

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        contentType="website"
        description={description}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'ItemPage',
          description: description,
          name: schemaTitle,
          image: schemaImages,
        }}
      >
        <LayoutSingleColumn className={css.pageRoot}>
          <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <div>
              <SectionImages
                title={title}
                listing={currentListing}
                isOwnListing={isOwnListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingType,
                  tab: listingTab,
                }}
                imageCarouselOpen={this.state.imageCarouselOpen}
                onImageCarouselClose={() => this.setState({ imageCarouselOpen: false })}
                handleViewPhotosClick={handleViewPhotosClick}
                onManageDisableScrolling={onManageDisableScrolling}
              />

              <div className={css.contentContainer}>
                <ContactPanel
                  className={css.bookingPanel}
                  listing={currentListing}
                  isOwnListing={isOwnListing}
                  unitType={unitType}
                  onSubmit={handleBookingSubmit}
                  title={bookingTitle}
                  subTitle={bookingSubTitle}
                  authorDisplayName={authorDisplayName}
                  onManageDisableScrolling={onManageDisableScrolling}
                  timeSlots={timeSlots}
                  fetchTimeSlotsError={fetchTimeSlotsError}
                  onFetchTransactionLineItems={onFetchTransactionLineItems}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  onContactUser={this.onContactUser}
                />

                <SectionAvatar user={currentAuthor} params={params} />

                <div className={css.mainContent}>
                  <div className={css.leftContent}>
                    <SectionHeading
                      priceTitle={removeCents(priceTitle)}
                      formattedPrice={removeCents(formattedPrice)}
                      richTitle={richTitle}
                      category={category}
                      hostLink={hostLink}
                      showContactUser={showContactUser}
                      onContactUser={this.onContactUser}
                      price={price}
                      publicData={publicData}
                      disableAllRootedRent={disableAllRootedRent}
                      intl={intl}
                    />
                    <div className={classNames(css.mobileAffordabillityEstimateCaculator)}>
                      {affordabilityEstimateCalculator}
                    </div>
                    <SectionDescriptionMaybe
                      address={address}
                      description={description}
                      currentUser={currentUser}
                      price={price}
                      intl={intl}
                      publicData={publicData}
                    />

                    {/* <SectionRulesMaybe publicData={publicData} /> */}
                    <SectionMapMaybe
                      geolocation={geolocation}
                      publicData={publicData}
                      listingId={currentListing.id}
                    />
                    {/* <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} /> */}
                    <SectionHostMaybe
                      title={title}
                      listing={currentListing}
                      authorDisplayName={authorDisplayName}
                      onContactUser={this.onContactUser}
                      isEnquiryModalOpen={isAuthenticated && this.state.enquiryModalOpen}
                      onCloseEnquiryModal={() => this.setState({ enquiryModalOpen: false })}
                      sendEnquiryError={sendEnquiryError}
                      sendEnquiryInProgress={sendEnquiryInProgress}
                      onSubmitEnquiry={this.onSubmitEnquiry}
                      currentUser={currentUser}
                      onManageDisableScrolling={onManageDisableScrolling}
                    />
                  </div>
                  <div
                    className={classNames(
                      css.rightContent,
                      css.desktopAffordabillityEstimateCaculator
                    )}
                  >
                    {affordabilityEstimateCalculator}
                  </div>
                </div>
              </div>
            </div>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ListingPageComponent.defaultProps = {
  unitType: config.bookingUnitType,
  currentUser: null,
  enquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  sendEnquiryError: null,
  filterConfig: config.custom.filters,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  unitType: propTypes.bookingUnitType,
  // from injectIntl
  intl: intlShape.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  enquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  sendEnquiryInProgress: bool.isRequired,
  sendEnquiryError: propTypes.error,
  onSendEnquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  filterConfig: array,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    timeSlots,
    fetchTimeSlotsError,
    sendEnquiryInProgress,
    sendEnquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    enquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const {
    disableAllRootedRent
  } = state.config

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    enquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    timeSlots,
    fetchTimeSlotsError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendEnquiryInProgress,
    sendEnquiryError,
    disableAllRootedRent,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: (bookingData, listingId, isOwnListing) =>
    dispatch(fetchTransactionLineItems(bookingData, listingId, isOwnListing)),
  onSendEnquiry: (listingId, message) => dispatch(sendEnquiry(listingId, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ListingPageComponent);

export default ListingPage;
